import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import apiClient from '../services/authService';
import { executeRecaptcha } from '../services/recaptcha';
import SEOMeta from './SEOMeta';
import pilotService from '../services/pilotService';
import {
  MessageSquare,
  Shield,
  Clock,
  TrendingUp,
  CheckCircle,
  Sparkles,
  Wrench,
  Bell,
  ArrowRight,
  Users,
  Zap,
} from 'lucide-react';
import PublicNavbar from './PublicNavbar';

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    portfolioSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [hasPilotAccess, setHasPilotAccess] = useState(false);

  // Check for pilot access on mount
  useEffect(() => {
    const checkPilotAccess = async () => {
      const result = await pilotService.checkPilotAccess();
      setHasPilotAccess(result.hasPilotAccess);
    };
    checkPilotAccess();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const recaptchaToken = await executeRecaptcha('lead_form');
      const response = await apiClient.post('/api/leads', {
        ...formData,
        recaptchaToken,
        source: 'landing-page'
      });

      if (response.data.success) {
        setSubmitMessage({
          type: 'success',
          text: response.data.message
        });
        setFormData({ name: '', email: '', portfolioSize: '' });

      } else {
        setSubmitMessage({
          type: 'error',
          text: response.data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({
        type: 'error',
        text: error.response?.data?.message || 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const landingSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'NivasaPro',
        url: 'https://nivasapro.ai',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description: 'AI-powered property management platform for Central Ohio landlords. Automate maintenance requests, tenant communication, and vendor coordination with agentic AI.',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free pilot access' },
        featureList: ['AI maintenance triage', '24/7 tenant support', 'Automated vendor dispatch', 'Owner approval workflow'],
      },
      {
        '@type': 'Organization',
        name: 'NivasaPro',
        url: 'https://nivasapro.ai',
        description: 'Building AI-powered property management tools for independent landlords and property managers.',
        areaServed: { '@type': 'State', name: 'Ohio', containedIn: 'United States' },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOMeta
        title="AI Property Management Software for Landlords"
        description="AI property management software that automates maintenance requests, tenant communication, and vendor dispatch, so landlords can manage more properties with less effort."
        canonical="/"
        schema={landingSchema}
      />
      <PublicNavbar activeRoute="/" hasPilotAccess={hasPilotAccess} />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-sm font-semibold tracking-wide text-cyan-700 uppercase">
              Agentic AI. Not Just Automation, Autonomous.
            </p>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
              AI Property Management Software.{' '}
              <span className="text-cyan-600">
                Built for Landlords Who'd Rather Not Manage.
              </span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed">
              Let AI agents handle the administrative heavy lifting: tenant communications, lease management, and maintenance coordination. Focus on growing your property portfolio, not paperwork.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>24/7 AI Agent</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>Auto Maintenance</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>Tenant Chat</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4">
              <Button
                size="lg"
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg"
                onClick={() => document.getElementById('lead-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Early Access <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900 transition-colors group"
              >
                Already invited?{' '}
                <span className="underline underline-offset-2 group-hover:no-underline">Sign in here</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Mock Chat Interface */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl blur-2xl opacity-20"></div>
            <div className="relative bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Niva - AI Agent</div>
                    <div className="text-xs text-green-600 flex items-center">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-1"></div>
                      Active
                    </div>
                  </div>
                </div>
                <MessageSquare className="h-5 w-5 text-gray-400" />
              </div>

              <div className="space-y-4">
                {/* Tenant Message */}
                <div className="flex justify-end">
                  <div className="bg-gray-100 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                    <p className="text-sm text-gray-800">
                      Hi, there's a pipe leak under my kitchen sink. Water is dripping slowly.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">2:34 PM</p>
                  </div>
                </div>

                {/* AI Response */}
                <div className="flex justify-start">
                  <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                    <p className="text-sm text-white">
                      Thanks for reporting this, Sarah. I've documented the issue and am coordinating with our plumber partner. 
                      Expected resolution: within 24 hours. I'll keep you updated!
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      <Wrench className="h-4 w-4 text-cyan-100" />
                      <span className="text-xs text-cyan-100">Maintenance scheduled</span>
                    </div>
                    <p className="text-xs text-cyan-100 mt-1">2:35 PM</p>
                  </div>
                </div>

                {/* Status Update */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Plumber dispatched</p>
                    <p className="text-xs text-green-700">ETA: Tomorrow 10 AM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16 md:py-24 border-y">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Scale Your Portfolio, Not Your Workload
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Reclaim 10+ hours per week. Let AI agents handle what takes you away from growth.
          </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
          <div className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
              <Clock className="h-6 w-6 text-cyan-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Save 10+ Hours Weekly</h3>
            <p className="text-gray-600">
              Stop spending your evenings on tenant emails and maintenance scheduling.
              Your AI agent handles 24/7 communications while you focus on strategic growth.
            </p>
          </div>

          <div className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Zap className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Instant Response Times</h3>
            <p className="text-gray-600">
              Eliminate delays. Tenant requests answered immediately, maintenance coordinated automatically,
              vendor follow-ups handled without manual intervention.
            </p>
          </div>

          <div className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Security-First Protocols</h3>
            <p className="text-gray-600">
              Bank-level encryption, SOC 2 compliance in progress, and data handling designed 
              specifically for property managers.
            </p>
          </div>

          <div className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Happier Tenants, Better Retention</h3>
            <p className="text-gray-600">
              Faster response times equal higher satisfaction. Better tenant retention means
              fewer turnovers and more time for portfolio growth, not constant replacements.
            </p>
          </div>

            <div className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">WhatsApp Integration</h3>
              <p className="text-gray-600">
                Tenants can report issues and get updates directly through WhatsApp. No app to download,
                no login required. Meet your tenants where they already are.
              </p>
            </div>

          <div className="p-6 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
              <Bell className="h-6 w-6 text-pink-600" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">The Future of Real Estate is Agentic</h3>
            <p className="text-gray-600">
              AI agents manage operations while you make strategic decisions. You get your life back.
              With NivasaPro, your properties run themselves.
            </p>
          </div>
            </div>
          </div>
        </section>

      {/* Pricing teaser — full pricing on /features */}
      <section id="pricing" className="py-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-base text-gray-600 mb-2">Plans start at <strong className="text-gray-900">$0</strong>, free forever for up to 2 units.</p>
          <p className="text-sm text-gray-500 mb-4">Starter from <strong>$29/mo</strong> · Professional from <strong>$79/mo</strong> · Portfolio custom pricing</p>
          <Button variant="outline" onClick={() => window.location.href = '/features#pricing'} className="border-cyan-500 text-cyan-600 hover:bg-cyan-50">
            See full pricing →
          </Button>
        </div>
      </section>

      {/* ROMI Score nudge */}
      <section className="py-10 bg-cyan-50 border-y border-cyan-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
            <div className="flex-1">
              <p className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mb-1">Free · 3 minutes · No signup</p>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Where does your operation actually stand?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Get your free NivasaPro ROMI Score™, a personalised maturity rating that shows exactly where you are and what to do next.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-cyan-600 text-cyan-700 hover:bg-cyan-600 hover:text-white whitespace-nowrap flex-shrink-0 px-6 py-5 font-semibold"
              onClick={() => navigate('/niva-romi-score')}
            >
              Evaluate My Operation <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section id="lead-form" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Get Early Access
              </h2>
              <p className="text-lg text-gray-600">
                Join the waitlist and we'll reach out within 24 hours to schedule your personalized demo.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-medium mb-2">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium mb-2">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="portfolioSize" className="text-gray-700 font-medium mb-2">
                    Portfolio Size *
                  </Label>
                  <select
                    id="portfolioSize"
                    name="portfolioSize"
                    required
                    value={formData.portfolioSize}
                    onChange={handleChange}
                    className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">Select number of units</option>
                    <option value="1-5">1-5 units</option>
                    <option value="6-20">6-20 units</option>
                    <option value="21+">21+ units</option>
                  </select>
                </div>

                {submitMessage.text && (
                  <div className={`p-4 rounded-lg ${
                    submitMessage.type === 'success' 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {submitMessage.text}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Early Access'}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to receive emails from NivasaPro.ai. Unsubscribe anytime.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
                <span className="text-xl font-bold text-white">NivasaPro.ai</span>
              </div>
              <p className="text-sm text-gray-400">
                Agentic rental property management powered by AI.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-cyan-400 transition text-left">Features</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Pricing</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Security</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-cyan-400 transition text-left">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-cyan-400 transition text-left">Blog</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Careers</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-cyan-400 transition text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-cyan-400 transition text-left">Terms of Service</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Contact</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 NivasaPro.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
